import '@formatjs/intl-segmenter/polyfill';
import { layout, layoutWithLines, prepare, prepareWithSegments, walkLineRanges } from '@chenglou/pretext';

/** 将相对行高换算为像素，并与旧文本布局保持向上取整语义。 */
const getPlainTextLineHeight = (fontSize: number, lineHeight: number) => Math.ceil(fontSize * lineHeight);

interface IGetPlainTextHeightByPretextArgs {
    fontSize: number;
    fontFamily: string;
    lineHeight: number;
    width: number;
}

interface IGetPlainTextWidthByPretextArgs {
    fontSize: number;
    fontFamily: string;
    bold?: boolean;
    italic?: boolean;
}

interface IGetPlainTextLayoutByPretextArgs {
    /** 字号，单位为像素。 */
    fontSize: number;
    /** CSS font-family，可包含多个回退字体。 */
    fontFamily: string;
    /** 已换算为像素的行高。 */
    lineHeightPx: number;
    /** 自动换行时可用的文本宽度。 */
    width: number;
    bold?: boolean;
    italic?: boolean;
    /** 为 true 时仅保留显式换行。 */
    disableAutoWrap?: boolean;
    /** `break-all` 按字素簇断行，避免拆开代理对或组合字符。 */
    wordBreak?: 'normal' | 'break-all';
}

/** 纯文本布局结果；`lines` 保留绘制每一行所需的文本和测量宽度。 */
interface IPlainTextLayoutByPretext {
    height: number;
    lineCount: number;
    lines: Array<{
        text: string;
        width: number;
    }>;
}

/** 将单个字体族格式化为可安全拼入 CSS font-family 的形式。 */
const formatFontFamilyName = (fontFamily: string) => {
    const name = fontFamily.trim();
    if (!name) return '';
    if (/^["'].*["']$/.test(name) || /^[\w-]+$/.test(name)) return name;
    return JSON.stringify(name);
};

/** 将字体族格式化为可安全拼入 Canvas/Pretext font shorthand 的形式。 */
const formatFontFamily = (fontFamily: string) =>
    fontFamily.split(',').map(formatFontFamilyName).filter(Boolean).join(', ');

/** 生成浏览器文字渲染共用的“子集、原字体、系统字体”回退链。 */
export const getFontFamilyWithFallback = (fontFamily: string, subsetFontFamily?: string | null) => {
    const sourceFamilies = fontFamily.split(',');
    const families = [subsetFontFamily, ...sourceFamilies, 'sans-serif']
        .map((item) => item?.trim())
        .filter((item): item is string => !!item);

    return [...new Set(families)].map(formatFontFamilyName).join(', ');
};

/** 生成 Canvas 与 Pretext 共用的 CSS font shorthand。 */
export const getPlainTextFontByPretext = ({
    fontSize,
    fontFamily,
    bold = false,
    italic = false,
}: Pick<IGetPlainTextLayoutByPretextArgs, 'fontSize' | 'fontFamily' | 'bold' | 'italic'>) => {
    const fontStyle = italic ? 'italic' : 'normal';
    const fontWeight = bold ? 'bold' : 'normal';
    return [fontStyle, fontWeight, fontSize + 'px', formatFontFamily(fontFamily)].join(' ');
};

type PlainTextMeasureContext = {
    font: string;
    measureText(text: string): {
        width: number;
        fontBoundingBoxAscent?: number;
        fontBoundingBoxDescent?: number;
    };
};

/** 页面生命周期内复用测量上下文和分词器，避免每个单元格重复创建。 */
let plainTextMeasureContext: PlainTextMeasureContext | null = null;
let graphemeSegmenter: Intl.Segmenter | null = null;

const getPlainTextMeasureContext = () => {
    if (plainTextMeasureContext) return plainTextMeasureContext;

    if (typeof OffscreenCanvas !== 'undefined') {
        try {
            plainTextMeasureContext = new OffscreenCanvas(1, 1).getContext('2d');
            if (plainTextMeasureContext) return plainTextMeasureContext;
        } catch {
            // Continue with the HTML canvas fallback.
        }
    }

    if (typeof document !== 'undefined') {
        try {
            plainTextMeasureContext = document.createElement('canvas').getContext('2d');
        } catch {
            return null;
        }
    }

    return plainTextMeasureContext;
};

/** 按用户可见字符切分，避免 `break-all` 将 emoji、代理对或组合字符拆开。 */
const getGraphemes = (text: string) => {
    graphemeSegmenter ??= new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(graphemeSegmenter.segment(text), ({ segment }) => segment);
};

export interface IPlainTextGraphemeMeasurement {
    text: string;
    width: number;
}

export interface IPlainTextFontMetrics {
    fontBoundingBoxAscent: number;
    fontBoundingBoxDescent: number;
}

/** 使用纯文本布局共用的 Canvas 上下文读取字体包围盒指标。 */
export const measurePlainTextFontMetrics = (
    text: string,
    args: Pick<IGetPlainTextLayoutByPretextArgs, 'fontSize' | 'fontFamily' | 'bold' | 'italic'>,
): IPlainTextFontMetrics | null => {
    const ctx = getPlainTextMeasureContext();
    if (!ctx) return null;

    ctx.font = getPlainTextFontByPretext(args);
    const metrics = ctx.measureText(text);
    const { fontBoundingBoxAscent, fontBoundingBoxDescent } = metrics;
    if (!Number.isFinite(fontBoundingBoxAscent) || !Number.isFinite(fontBoundingBoxDescent)) return null;

    return {
        fontBoundingBoxAscent: fontBoundingBoxAscent!,
        fontBoundingBoxDescent: fontBoundingBoxDescent!,
    };
};

/** 使用与 Pretext 相同的 Canvas font 测量字素簇，供需要手动字距的绘制链路复用。 */
export const measurePlainTextGraphemes = (
    text: string,
    args: Pick<IGetPlainTextLayoutByPretextArgs, 'fontSize' | 'fontFamily' | 'bold' | 'italic'>,
): IPlainTextGraphemeMeasurement[] | null => {
    const ctx = getPlainTextMeasureContext();
    if (!ctx) return null;

    ctx.font = getPlainTextFontByPretext(args);
    return getGraphemes(text).map((grapheme) => ({
        text: grapheme,
        width: ctx.measureText(grapheme).width,
    }));
};

/**
 * 使用 Canvas 实测每个候选行，补足 Pretext 默认分词无法提供的 `break-all` 语义。
 */
const getPlainTextBreakAllLayout = (
    text: string,
    args: IGetPlainTextLayoutByPretextArgs,
    ctx: PlainTextMeasureContext,
): IPlainTextLayoutByPretext => {
    const font = getPlainTextFontByPretext(args);
    ctx.font = font;

    const maxWidth = Math.max(1, args.width);
    const lines: IPlainTextLayoutByPretext['lines'] = [];
    const hardLines = text.replace(/\r\n?/g, '\n').split('\n');

    hardLines.forEach((hardLine) => {
        if (args.disableAutoWrap) {
            lines.push({ text: hardLine, width: ctx.measureText(hardLine).width });
            return;
        }

        const graphemes = getGraphemes(hardLine);
        if (!graphemes.length) {
            lines.push({ text: '', width: 0 });
            return;
        }

        let lineText = '';
        let lineWidth = 0;
        graphemes.forEach((grapheme) => {
            const candidate = lineText + grapheme;
            const candidateWidth = ctx.measureText(candidate).width;

            if (lineText && candidateWidth > maxWidth) {
                lines.push({ text: lineText, width: lineWidth });
                lineText = grapheme;
                lineWidth = ctx.measureText(grapheme).width;
                return;
            }

            lineText = candidate;
            lineWidth = candidateWidth;
        });
        lines.push({ text: lineText, width: lineWidth });
    });

    return {
        height: lines.length * args.lineHeightPx,
        lineCount: lines.length,
        lines,
    };
};

const getPlainTextPretextLayout = (text: string, args: IGetPlainTextLayoutByPretextArgs): IPlainTextLayoutByPretext => {
    const font = getPlainTextFontByPretext(args);
    const prepared = prepareWithSegments(text, font, { whiteSpace: 'pre-wrap' });
    const maxWidth = args.disableAutoWrap ? Number.POSITIVE_INFINITY : Math.max(1, args.width);
    const result = layoutWithLines(prepared, maxWidth, args.lineHeightPx);

    return {
        height: result.height,
        lineCount: result.lineCount,
        lines: result.lines.map(({ text, width }) => ({ text, width })),
    };
};

/**
 * 计算纯文本的逐行布局；空文本返回零行，是否保留一行高度由上层业务决定。
 */
export const getPlainTextLayoutByPretext = (
    text: string,
    args: IGetPlainTextLayoutByPretextArgs,
): IPlainTextLayoutByPretext => {
    if (!text) {
        return {
            height: 0,
            lineCount: 0,
            lines: [],
        };
    }

    // 无 Canvas 环境时退回 Pretext，确保 SSR/测试环境仍可完成基础布局。
    const measureContext = args.wordBreak === 'break-all' ? getPlainTextMeasureContext() : null;
    return measureContext
        ? getPlainTextBreakAllLayout(text, args, measureContext)
        : getPlainTextPretextLayout(text, args);
};

/**
 * 通过预处理文本计算文本高度
 * @param text - 文本
 * @param args - 文本参数
 * @returns 文本高度
 */
export const getPlainTextHeightByPretext = (text: string, args: IGetPlainTextHeightByPretextArgs) => {
    const { fontSize, fontFamily, lineHeight: lineHeightValue, width } = args;
    const lineHeight = getPlainTextLineHeight(fontSize, lineHeightValue);

    if (!text) return lineHeight;

    const font = `${args.fontSize}px ${fontFamily}`;
    const prepared = prepare(text, font, { whiteSpace: 'pre-wrap' });
    return Math.max(lineHeight, Math.ceil(layout(prepared, width, lineHeight).height));
};

/**
 * 通过预处理文本计算文本宽度
 * @param text - 文本
 * @param args - 文本参数
 * @returns 文本中最宽一行的宽度
 */
export const getPlainTextWidthByPretext = (text: string, args: IGetPlainTextWidthByPretextArgs) => {
    if (!text) return 0;

    const font = getPlainTextFontByPretext(args);
    const prepared = prepareWithSegments(text, font, { whiteSpace: 'pre-wrap' });
    let width = 0;

    walkLineRanges(prepared, Number.POSITIVE_INFINITY, (line) => {
        width = Math.max(width, line.width);
    });

    return width;
};
